import React, { useEffect, useState, useCallback, createContext, useContext, useRef } from 'react';
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number; // ms — default 4000, 0 = indefinite
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType, duration?: number) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue>({ showToast: () => {} });

export const useToast = () => useContext(ToastContext);

// ─────────────────────────────────────────────────────────────────────────────
// Single toast item
// ─────────────────────────────────────────────────────────────────────────────

const ICONS: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle2 size={16} className="text-green-400 flex-shrink-0" />,
  error:   <AlertCircle  size={16} className="text-red-400 flex-shrink-0" />,
  info:    <Info         size={16} className="text-blue-400 flex-shrink-0" />,
  warning: <AlertTriangle size={16} className="text-amber-400 flex-shrink-0" />,
};

const STYLES: Record<ToastType, string> = {
  success: 'bg-slate-900/95 border-green-600/40',
  error:   'bg-slate-900/95 border-red-600/40',
  info:    'bg-slate-900/95 border-blue-600/40',
  warning: 'bg-slate-900/95 border-amber-600/40',
};

const ToastItem: React.FC<{ toast: Toast; onDismiss: (id: string) => void }> = ({ toast, onDismiss }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Animate in
    const enterTimer = setTimeout(() => setVisible(true), 10);
    // Auto-dismiss
    if (toast.duration !== 0) {
      const exitTimer = setTimeout(() => {
        setVisible(false);
        setTimeout(() => onDismiss(toast.id), 300);
      }, toast.duration ?? 4000);
      return () => { clearTimeout(enterTimer); clearTimeout(exitTimer); };
    }
    return () => clearTimeout(enterTimer);
  }, [toast.id, toast.duration, onDismiss]);

  return (
    <div
      className={`
        flex items-start gap-3 px-4 py-3 rounded-lg border shadow-xl backdrop-blur-sm
        max-w-sm w-full pointer-events-auto
        transition-all duration-300
        ${STYLES[toast.type]}
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
      `}
    >
      {ICONS[toast.type]}
      <p className="text-sm text-slate-200 flex-1 leading-snug">{toast.message}</p>
      <button
        onClick={() => { setVisible(false); setTimeout(() => onDismiss(toast.id), 300); }}
        className="text-slate-500 hover:text-slate-300 transition-colors mt-0.5 flex-shrink-0"
      >
        <X size={14} />
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Provider + container
// ─────────────────────────────────────────────────────────────────────────────

export const AppToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const showToast = useCallback((message: string, type: ToastType = 'info', duration?: number) => {
    const id = `toast-${++idRef.current}`;
    setToasts(prev => [...prev, { id, type, message, duration }]);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {/* Toast container — bottom-center. Bottom-right hid behind the
          right-edge floating panels when Studio was in full-screen
          landscape on iPad; centred clears the panels and reads as a
          system message. safe-area-inset-bottom keeps it above the home
          indicator in standalone PWA mode. */}
      <div
        className="fixed z-[10000] flex flex-col gap-2 items-center pointer-events-none"
        style={{
          left: '50%',
          transform: 'translateX(-50%)',
          bottom: 'max(24px, calc(env(safe-area-inset-bottom) + 16px))',
        }}
      >
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};
